import { colorPairAt, nextColorPairIndex } from "./colors.js";
import { pictureFor, trackPictureSeen } from "./letter-pictures.js";
import {
  emptyTextStat,
  LETTER_FADE_START_LEVEL,
  LETTER_GONE_LEVEL,
  LETTERS_MAX_LEVEL,
  MAX_LEVEL,
  MIN_LEVEL,
  Profile,
  ReadingBand,
  ReadingEvent,
  ReadingText,
  ResultKind,
  SOUND_MASTERY_MAX_LEVEL,
  TextStat,
  WORDS_INTRO_LEVEL,
} from "./type.app.js";
import { createId } from "./util.id.js";

export const STARTING_LEVEL: Record<ReadingBand, number> = {
  letters: 1,
  words: 11,
  phrases: 29,
  sentences: 47,
  books: 74,
};

const LEGACY_STARTING_LEVEL: Record<Exclude<ReadingBand, "letters">, number> = {
  words: 1,
  phrases: 22,
  sentences: 42,
  books: 72,
};

export const ALGORITHM = {
  retireCorrectCount: 3,
  levelUpStreak: 10,
  /** Per-sound consecutive correct answers required through level 13. */
  soundMasteryStreak: 5,
  /** Attempt count at the working level, not unique corpus texts. */
  levelUpMinSeen: 75,
  levelUpRecentWindow: 50,
  levelUpRecentCorrect: 45,
  easyJump: 8,
  reviewMaxDepth: 8,
  mixReviewDepth: 5,
  mixPreviewDepth: 3,
  mixCurrentBias: 2,
  wrongCooldownMin: 2,
  wrongCooldownMax: 5,
  letterWrongCooldownMin: 4,
  letterWrongCooldownMax: 7,
  recentWindow: 3,
  correctWeight: 0.4,
  wrongWeight: 2.4,
} as const;

export const isLetterLevel = (level: number): boolean => level <= LETTERS_MAX_LEVEL;
export const isSoundMasteryLevel = (level: number): boolean => level <= SOUND_MASTERY_MAX_LEVEL;

/** Chance a prompt is an isolated letter/combo while fading from level 11 to 13. */
export const letterPromptChance = (level: number): number => {
  if (level >= LETTER_GONE_LEVEL) return 0;
  if (level <= LETTER_FADE_START_LEVEL) return 1;
  return (LETTER_GONE_LEVEL - level) / (LETTER_GONE_LEVEL - LETTER_FADE_START_LEVEL);
};

export const bandFloor = (level: number): number => {
  if (level < STARTING_LEVEL.words) {
    return level < WORDS_INTRO_LEVEL ? STARTING_LEVEL.letters : WORDS_INTRO_LEVEL;
  }
  if (level < STARTING_LEVEL.phrases) return STARTING_LEVEL.words;
  if (level < STARTING_LEVEL.sentences) return STARTING_LEVEL.phrases;
  if (level < STARTING_LEVEL.books) return STARTING_LEVEL.sentences;
  return STARTING_LEVEL.books;
};

export type Rng = {
  random: () => number;
  int: (min: number, max: number) => number;
};

export const defaultRng: Rng = {
  random: () => Math.random(),
  int: (min, max) => min + Math.floor(Math.random() * (max - min + 1)),
};

export const clampLevel = (level: number): number => {
  return Math.min(MAX_LEVEL, Math.max(MIN_LEVEL, Math.round(level)));
};

const remapRange = (level: number, oldMin: number, oldMax: number, newMin: number, newMax: number): number => {
  return Math.round(newMin + ((level - oldMin) * (newMax - newMin)) / (oldMax - oldMin));
};

export const remapLegacyLevel = (level: number): number => {
  const clamped = clampLevel(level);
  if (clamped <= 20) return remapRange(clamped, 1, 20, 11, 28);
  if (clamped <= 40) return remapRange(clamped, 21, 40, 29, 46);
  if (clamped <= 70) return remapRange(clamped, 41, 70, 47, 73);
  return remapRange(clamped, 71, 100, 74, 100);
};

export const migrateProfileFromV1 = (profile: Profile): Profile => {
  const level = remapLegacyLevel(profile.level);
  return {
    ...profile,
    level,
    boostLevel: remapLegacyLevel(profile.boostLevel),
    currentTextId: null,
    lastTextId: null,
    recentTextIds: [],
    textStats: {},
  };
};

export const normalizeImportedProfile = (profile: Profile): Profile => {
  if (profile.band === "letters") {
    return profile;
  }
  if (profile.events.length > 0) {
    return profile;
  }
  if (profile.level !== LEGACY_STARTING_LEVEL[profile.band]) {
    return profile;
  }
  const level = STARTING_LEVEL[profile.band];
  return {
    ...profile,
    level,
    boostLevel: level,
    currentTextId: null,
  };
};

const mergeTextStat = (current: TextStat, incoming: TextStat, textId: string): TextStat => {
  const currentPicture = current.lastPictureAt;
  const incomingPicture = incoming.lastPictureAt;
  const lastPictureAt =
    currentPicture && incomingPicture
      ? currentPicture > incomingPicture
        ? currentPicture
        : incomingPicture
      : (currentPicture ?? incomingPicture);
  return {
    textId,
    correct: current.correct + incoming.correct,
    wrong: current.wrong + incoming.wrong,
    skip: current.skip + incoming.skip,
    wayTooEasy: current.wayTooEasy + incoming.wayTooEasy,
    cooldown: Math.max(current.cooldown, incoming.cooldown),
    retired: current.retired || incoming.retired,
    lastPictureAt,
    pictureRefreshLeft: Math.max(current.pictureRefreshLeft ?? 0, incoming.pictureRefreshLeft ?? 0),
  };
};

export const remapProfileCorpusIds = (profile: Profile, texts: ReadingText[]): Profile => {
  const byId = new Map(texts.map((text) => [text.id, text]));
  const letterByText = new Map(
    texts.filter((text) => text.kind === "letter").map((text) => [text.text.trim().toLowerCase(), text]),
  );

  const resolve = (id: string | null): string | null => {
    if (!id) {
      return null;
    }
    if (byId.has(id)) {
      return id;
    }
    const event = [...profile.events].reverse().find((item) => item.textId === id);
    if (!event) {
      return null;
    }
    const letter = letterByText.get(event.text.trim().toLowerCase());
    if (letter) {
      return letter.id;
    }
    const match = texts.find(
      (text) => text.level === event.level && text.text.trim().toLowerCase() === event.text.trim().toLowerCase(),
    );
    return match?.id ?? null;
  };

  const mappedIds = new Map<string, string>();
  const remember = (id: string | null): void => {
    if (!id || mappedIds.has(id)) {
      return;
    }
    const next = resolve(id);
    if (next && next !== id) {
      mappedIds.set(id, next);
    }
  };

  remember(profile.currentTextId);
  remember(profile.lastTextId);
  for (const id of profile.recentTextIds) remember(id);
  for (const event of profile.events) remember(event.textId);
  for (const id of Object.keys(profile.textStats)) remember(id);

  if (mappedIds.size === 0) {
    const current = resolve(profile.currentTextId);
    if (current === profile.currentTextId) {
      return profile;
    }
    return { ...profile, currentTextId: current };
  }

  const mapId = (id: string): string => mappedIds.get(id) ?? id;
  const textStats: Record<string, TextStat> = {};
  for (const [id, stat] of Object.entries(profile.textStats)) {
    const nextId = mapId(id);
    const incoming = { ...stat, textId: nextId };
    textStats[nextId] = textStats[nextId] ? mergeTextStat(textStats[nextId], incoming, nextId) : incoming;
  }

  return {
    ...profile,
    currentTextId: resolve(profile.currentTextId),
    lastTextId: resolve(profile.lastTextId),
    recentTextIds: profile.recentTextIds.map(mapId),
    events: profile.events.map((event) => ({ ...event, textId: mapId(event.textId) })),
    textStats,
  };
};

export const setExactLevel = (profile: Profile, level: number): Profile => {
  const next = clampLevel(level);
  return {
    ...profile,
    level: next,
    boostLevel: next,
    boostActive: false,
    correctStreak: 0,
    wrongStreak: 0,
    currentTextId: null,
  };
};

export const createProfile = (
  name: string,
  band: ReadingBand,
  usedColorIndexes: number[],
  now = new Date(),
  colorPairIndex = nextColorPairIndex(usedColorIndexes),
): Profile => {
  const colors = colorPairAt(colorPairIndex);
  const level = STARTING_LEVEL[band];
  return {
    id: createId(),
    name: name.trim(),
    colorPairIndex,
    primaryColor: colors.primary,
    secondaryColor: colors.secondary,
    band,
    level,
    manuallySetLevel: false,
    createdAt: now.toISOString(),
    currentTextId: null,
    lastTextId: null,
    recentTextIds: [],
    boostActive: false,
    boostLevel: level,
    correctStreak: 0,
    wrongStreak: 0,
    textStats: {},
    events: [],
    coins: 0,
    coinsEarned: 0,
    peakCoins: 0,
    inventory: [],
    itemPositions: {},
    correctsUntilCoin: 0,
    coinAwardsUntilBonus: defaultRng.int(10, 15),
    maxCorrectStreak: 0,
    achievements: [],
    unlockedLocations: ["start"],
    currentLocationId: "start",
    selectedSubject: "reading",
    subjectProgress: {
      reading: {
        level,
        boostLevel: level,
        correctStreak: 0,
        wrongStreak: 0,
        maxCorrectStreak: 0,
        correctAnswers: 0,
        itemCorrect: {},
        mathSeen: {},
        mathWrong: {},
        mathHintHidden: {},
        mathReviewCount: 0,
        mathReviewInterval: 3,
        currentTextId: null,
        events: [],
      },
      math: {
        level: MIN_LEVEL,
        boostLevel: MIN_LEVEL,
        correctStreak: 0,
        wrongStreak: 0,
        maxCorrectStreak: 0,
        correctAnswers: 0,
        itemCorrect: {},
        mathSeen: {},
        mathWrong: {},
        mathHintHidden: {},
        mathReviewCount: 0,
        mathReviewInterval: 3,
        currentTextId: null,
        events: [],
      },
      spelling: {
        level: MIN_LEVEL,
        boostLevel: MIN_LEVEL,
        correctStreak: 0,
        wrongStreak: 0,
        maxCorrectStreak: 0,
        correctAnswers: 0,
        itemCorrect: {},
        mathSeen: {},
        mathWrong: {},
        mathHintHidden: {},
        mathReviewCount: 0,
        mathReviewInterval: 3,
        currentTextId: null,
        events: [],
      },
    },
  };
};

export const applyColorPair = (profile: Profile, colorPairIndex: number): Profile => {
  const colors = colorPairAt(colorPairIndex);
  return {
    ...profile,
    colorPairIndex,
    primaryColor: colors.primary,
    secondaryColor: colors.secondary,
  };
};

export const workingLevel = (profile: Profile): number => {
  return profile.boostActive ? profile.boostLevel : profile.level;
};

const getStat = (profile: Profile, textId: string): TextStat => {
  return profile.textStats[textId] ?? emptyTextStat(textId);
};

const tickCooldowns = (stats: Record<string, TextStat>, exceptId: string): Record<string, TextStat> => {
  const next: Record<string, TextStat> = {};
  for (const [id, stat] of Object.entries(stats)) {
    if (id === exceptId) {
      next[id] = stat;
      continue;
    }
    next[id] = stat.cooldown > 0 ? { ...stat, cooldown: stat.cooldown - 1 } : stat;
  }
  return next;
};

const rememberRecent = (recent: string[], textId: string): string[] => {
  const next = [textId, ...recent.filter((id) => id !== textId)];
  return next.slice(0, ALGORITHM.recentWindow);
};

export const textWeight = (stat: TextStat): number => {
  if (stat.retired) {
    return 0;
  }
  return Math.pow(ALGORITHM.correctWeight, stat.correct) * Math.pow(ALGORITHM.wrongWeight, stat.wrong);
};

const weightedPick = (
  texts: ReadingText[],
  profile: Profile,
  rng: Rng,
  extraWeight: (text: ReadingText) => number = () => 1,
): ReadingText => {
  const weights = texts.map((text) => {
    const stat = getStat(profile, text.id);
    const extra = extraWeight(text);
    const base = textWeight(stat) === 0 && stat.retired && extra > 1 ? 1 : textWeight(stat);
    return Math.max(0.0001, base * extra);
  });
  const sum = weights.reduce((total, weight) => total + weight, 0);
  let cursor = rng.random() * sum;
  for (let i = 0; i < texts.length; i += 1) {
    cursor -= weights[i];
    if (cursor <= 0) {
      return texts[i];
    }
  }
  return texts[texts.length - 1];
};

export const mixLevelWeight = (textLevel: number, target: number): number => {
  const delta = textLevel - target;
  if (delta === 0) return ALGORITHM.mixCurrentBias;
  if (delta < 0) return 0.55 / Math.abs(delta);
  return 0.45 / delta;
};

const mixFloor = (target: number): number => {
  if (target < STARTING_LEVEL.words) return WORDS_INTRO_LEVEL;
  return STARTING_LEVEL.words;
};

const isMixEligible = (profile: Profile, text: ReadingText, target: number): boolean => {
  const stat = getStat(profile, text.id);
  if (stat.cooldown > 0) {
    return false;
  }
  if (text.id === profile.lastTextId) {
    return false;
  }
  if (profile.recentTextIds.includes(text.id)) {
    return false;
  }
  if (text.level >= target && stat.retired) {
    const confidence = isLetterLevel(target) && profile.wrongStreak > 0 && text.level === target;
    const keepVolume = text.level === target && needsLevelVolume(profile);
    if (!confidence && !keepVolume) {
      return false;
    }
  }
  return true;
};

const mixCoverageBoost = (profile: Profile, text: ReadingText, target: number): number => {
  if (isLetterLevel(target)) {
    if (profile.wrongStreak > 0 && hasMasteredText(profile, text.id) && text.level <= target) {
      return 2.4;
    }
    if (text.level !== target) {
      return 1;
    }
    if (!hasSeenText(profile, text.id)) {
      return 6;
    }
    if (!hasMasteredText(profile, text.id)) {
      return 3;
    }
    return 1;
  }
  if (text.level !== target) {
    return 1;
  }
  if (!hasSeenText(profile, text.id)) {
    return 6;
  }
  if (needsLevelVolume(profile)) {
    return 3;
  }
  return 1;
};

export const hasSeenText = (profile: Profile, textId: string): boolean => {
  const stat = profile.textStats[textId];
  if (!stat) {
    return false;
  }
  return stat.correct + stat.wrong + stat.skip + stat.wayTooEasy > 0;
};

export const hasMasteredText = (profile: Profile, textId: string): boolean => {
  const stat = profile.textStats[textId];
  return Boolean(stat && (stat.correct > 0 || stat.wayTooEasy > 0));
};

export const latestResult = (profile: Profile, textId: string): ResultKind | null => {
  for (let i = profile.events.length - 1; i >= 0; i -= 1) {
    const event = profile.events[i];
    if (event.textId === textId && event.result !== "skip") {
      return event.result;
    }
  }
  return null;
};

export const consecutiveCorrect = (profile: Profile, textId: string): number => {
  let streak = 0;
  for (let i = profile.events.length - 1; i >= 0; i -= 1) {
    const event = profile.events[i];
    if (event.textId !== textId || event.result === "skip") {
      continue;
    }
    if (event.result === "right" || event.result === "wayTooEasy") {
      streak += 1;
      continue;
    }
    break;
  }
  return streak;
};

export const hasActiveSoundStreak = (profile: Profile, textId: string): boolean => {
  if (latestResult(profile, textId) === "wayTooEasy") {
    return true;
  }
  return consecutiveCorrect(profile, textId) >= ALGORITHM.soundMasteryStreak;
};

export const introducedSounds = (texts: ReadingText[], level: number): ReadingText[] => {
  const cap = Math.min(level, LETTERS_MAX_LEVEL);
  return texts.filter((text) => text.kind === "letter" && text.level <= cap);
};

export const soundStreaksComplete = (
  profile: Profile,
  texts: ReadingText[],
  level = workingLevel(profile),
): boolean => {
  if (level >= LETTER_GONE_LEVEL || profile.manuallySetLevel) {
    if (profile.manuallySetLevel && level < LETTER_GONE_LEVEL) {
      const currentSounds = introducedSounds(texts, level).filter((text) => text.level >= level);
      return currentSounds.every((text) => hasActiveSoundStreak(profile, text.id));
    }
    return true;
  }
  const sounds = introducedSounds(texts, level);
  if (sounds.length === 0) {
    return true;
  }
  return sounds.every((text) => hasActiveSoundStreak(profile, text.id));
};

export const needsSupport = (profile: Profile, textId: string): boolean => {
  return latestResult(profile, textId) === "wrong";
};

const isCorrectResult = (result: ResultKind): boolean => result === "right" || result === "wayTooEasy";

export const eventsAtLevel = (profile: Profile, level: number): ReadingEvent[] => {
  return profile.events.filter((event) => event.level === level);
};

export type LevelUpGate = {
  streak: number;
  streakNeeded: number;
  seen: number;
  seenNeeded: number;
  recentCorrect: number;
  recentCount: number;
  recentNeeded: number;
  recentWindow: number;
  coverageOk: boolean;
  soundsMastered: number;
  soundsNeeded: number;
  ready: boolean;
};

export const levelUpGate = (
  profile: Profile,
  corpus: ReadingText[] = [],
  streak = profile.correctStreak,
): LevelUpGate => {
  const level = workingLevel(profile);
  const atLevel = eventsAtLevel(profile, level);
  const scored = atLevel.filter((event) => event.result !== "skip");
  const window = scored.slice(-ALGORITHM.levelUpRecentWindow);
  const recentCorrect = window.filter((event) => isCorrectResult(event.result)).length;
  const soundLevel = isSoundMasteryLevel(level);
  const sounds = introducedSounds(corpus, level);
  const requiredSounds = profile.manuallySetLevel ? sounds.filter((text) => text.level >= level) : sounds;
  const soundsMastered = requiredSounds.filter((text) => hasActiveSoundStreak(profile, text.id)).length;
  const soundsNeeded = level >= LETTER_GONE_LEVEL ? 0 : requiredSounds.length;
  const coverageOk = soundStreaksComplete(profile, corpus, level);
  const volumeOk =
    soundLevel ||
    (atLevel.length >= ALGORITHM.levelUpMinSeen &&
      window.length >= ALGORITHM.levelUpRecentWindow &&
      recentCorrect >= ALGORITHM.levelUpRecentCorrect);
  const streakOk = soundLevel || streak >= ALGORITHM.levelUpStreak;
  return {
    streak,
    streakNeeded: soundLevel ? ALGORITHM.soundMasteryStreak : ALGORITHM.levelUpStreak,
    seen: atLevel.length,
    seenNeeded: ALGORITHM.levelUpMinSeen,
    recentCorrect,
    recentCount: window.length,
    recentNeeded: ALGORITHM.levelUpRecentCorrect,
    recentWindow: ALGORITHM.levelUpRecentWindow,
    coverageOk,
    soundsMastered,
    soundsNeeded,
    ready: coverageOk && volumeOk && streakOk,
  };
};

const needsLevelVolume = (profile: Profile): boolean => {
  if (isSoundMasteryLevel(workingLevel(profile))) {
    return false;
  }
  const gate = levelUpGate(profile);
  return gate.seen < gate.seenNeeded || gate.recentCount < gate.recentWindow;
};

const pickFrom = (
  texts: ReadingText[],
  profile: Profile,
  rng: Rng,
  eligible: (text: ReadingText) => boolean,
): ReadingText | null => {
  const pool = texts.filter(eligible);
  if (pool.length === 0) {
    return null;
  }
  return weightedPick(pool, profile, rng);
};

const pickReview = (profile: Profile, corpus: ReadingText[], rng: Rng): ReadingText | null => {
  const target = workingLevel(profile);
  const floor = bandFloor(target);
  const depth = Math.min(Math.max(profile.wrongStreak, 1), ALGORITHM.reviewMaxDepth);
  const tryLevel = (level: number, requireNotRecent: boolean): ReadingText | null => {
    const pool = corpus.filter((text) => {
      if (text.level !== level) {
        return false;
      }
      if (!hasMasteredText(profile, text.id)) {
        return false;
      }
      if (text.id === profile.lastTextId) {
        return false;
      }
      if (requireNotRecent && profile.recentTextIds.includes(text.id)) {
        return false;
      }
      return true;
    });
    return pool.length > 0 ? weightedPick(pool, profile, rng) : null;
  };
  for (const requireNotRecent of [true, false]) {
    for (let distance = 1; distance <= depth; distance += 1) {
      const level = target - distance;
      if (level < floor) {
        break;
      }
      const picked = tryLevel(level, requireNotRecent);
      if (picked) {
        return picked;
      }
    }
  }
  return null;
};

const uniqueTexts = (texts: ReadingText[]): ReadingText[] => {
  const seen = new Set<string>();
  const next: ReadingText[] = [];
  for (const text of texts) {
    if (seen.has(text.id)) continue;
    seen.add(text.id);
    next.push(text);
  }
  return next;
};

const earlyMixPool = (corpus: ReadingText[], target: number): ReadingText[] => {
  const sounds = introducedSounds(corpus, target);
  const current = corpus.filter((text) => text.level === target);
  return uniqueTexts([...sounds, ...current]);
};

const isEarlyMixEligible = (profile: Profile, text: ReadingText, relax: "strict" | "repeat" | "cooldown"): boolean => {
  const stat = getStat(profile, text.id);
  if (relax !== "cooldown" && stat.cooldown > 0) {
    return false;
  }
  if (text.id === profile.lastTextId) {
    return false;
  }
  if (relax === "strict" && profile.recentTextIds.includes(text.id)) {
    return false;
  }
  return true;
};

const pickUniform = (
  texts: ReadingText[],
  profile: Profile,
  rng: Rng,
  relax: "strict" | "repeat" | "cooldown",
): ReadingText | null => {
  const pool = texts.filter((text) => isEarlyMixEligible(profile, text, relax));
  if (pool.length === 0) {
    return null;
  }
  return pool[rng.int(0, pool.length - 1)];
};

const pickEqualMix = (profile: Profile, pool: ReadingText[], rng: Rng, fallback: ReadingText[]): ReadingText => {
  const needsHelp = pool.filter((text) => needsSupport(profile, text.id));
  const retry = pickUniform(needsHelp, profile, rng, "strict") ?? pickUniform(needsHelp, profile, rng, "repeat");
  if (retry) {
    return retry;
  }
  return (
    pickUniform(pool, profile, rng, "strict") ??
    pickUniform(pool, profile, rng, "repeat") ??
    pickUniform(pool, profile, rng, "cooldown") ??
    pool.find((text) => text.id !== profile.lastTextId) ??
    pool[0] ??
    fallback[rng.int(0, Math.max(0, fallback.length - 1))]
  );
};

const pickEarlyMix = (profile: Profile, corpus: ReadingText[], rng: Rng): ReadingText => {
  const target = workingLevel(profile);
  const pool = earlyMixPool(corpus, target);
  if (pool.length === 0) {
    return pickMixed(profile, corpus, rng);
  }
  return pickEqualMix(profile, pool, rng, corpus);
};

const pickMixed = (profile: Profile, corpus: ReadingText[], rng: Rng): ReadingText => {
  const target = workingLevel(profile);
  const extraWeight = (text: ReadingText): number =>
    mixLevelWeight(text.level, target) * mixCoverageBoost(profile, text, target);
  const pickWindow = (from: number, to: number): ReadingText | null => {
    const pool = corpus.filter((text) => {
      if (text.level < from || text.level > to || !isMixEligible(profile, text, target)) {
        return false;
      }
      return true;
    });
    if (pool.length === 0) {
      return null;
    }
    return weightedPick(pool, profile, rng, extraWeight);
  };

  const from = Math.max(mixFloor(target), target - ALGORITHM.mixReviewDepth);
  const to = Math.min(MAX_LEVEL, target + ALGORITHM.mixPreviewDepth);
  const mixed = pickWindow(from, to);
  if (mixed) {
    return mixed;
  }

  for (let radius = ALGORITHM.mixPreviewDepth + 1; radius <= MAX_LEVEL; radius += 1) {
    const found = pickWindow(Math.max(MIN_LEVEL, target - radius), Math.min(MAX_LEVEL, target + radius));
    if (found) {
      return found;
    }
  }

  const notLast = corpus.filter((text) => text.id !== profile.lastTextId);
  const fallback = notLast.length > 0 ? notLast : corpus;
  return fallback[rng.int(0, fallback.length - 1)];
};

const pickOpenLevel = (profile: Profile, corpus: ReadingText[], rng: Rng): ReadingText => {
  const target = workingLevel(profile);
  if (!isLetterLevel(target)) {
    const atLevel = corpus.filter((text) => text.level === target);
    const unseen = pickFrom(atLevel, profile, rng, (text) => {
      return isMixEligible(profile, text, target) && !hasSeenText(profile, text.id);
    });
    if (unseen) {
      return unseen;
    }
  }
  return pickMixed(profile, corpus, rng);
};

export const pickNext = (profile: Profile, corpus: ReadingText[], rng: Rng = defaultRng): ReadingText => {
  if (corpus.length === 0) {
    throw new Error("Reading Bee corpus is empty.");
  }
  const target = workingLevel(profile);
  if (target < LETTER_GONE_LEVEL) {
    if (target <= LETTER_FADE_START_LEVEL) {
      return pickEarlyMix(profile, corpus, rng);
    }
    const sounds = introducedSounds(corpus, target);
    if (sounds.length > 0 && rng.random() < letterPromptChance(target)) {
      return pickEqualMix(profile, sounds, rng, corpus);
    }
  }
  const rest = corpus.filter((text) => text.kind !== "letter");
  const open = rest.length > 0 ? rest : corpus;
  if (profile.wrongStreak > 0) {
    const reviewed = pickReview(profile, open, rng);
    if (reviewed) {
      return reviewed;
    }
  }
  return pickOpenLevel(profile, open, rng);
};

export const ensureCurrentText = (profile: Profile, corpus: ReadingText[], rng: Rng = defaultRng): Profile => {
  if (profile.currentTextId && corpus.some((text) => text.id === profile.currentTextId)) {
    return profile;
  }
  const next = pickNext(profile, corpus, rng);
  return { ...profile, currentTextId: next.id };
};

export const applyResult = (
  profile: Profile,
  text: ReadingText,
  result: ResultKind,
  rng: Rng = defaultRng,
  at = new Date(),
  corpus: ReadingText[] = [],
): Profile => {
  const event: ReadingEvent = {
    id: createId(),
    textId: text.id,
    text: text.text,
    level: text.level,
    result,
    at: at.toISOString(),
  };

  let stats = tickCooldowns(profile.textStats, text.id);
  const stat = { ...(stats[text.id] ?? emptyTextStat(text.id)) };
  const next: Profile = {
    ...profile,
    textStats: stats,
    events: [...profile.events, event],
    lastTextId: text.id,
    recentTextIds: rememberRecent(profile.recentTextIds, text.id),
    currentTextId: null,
  };

  if (result === "skip") {
    stat.skip += 1;
    stats = { ...stats, [text.id]: stat };
    return { ...next, textStats: stats };
  }

  if (result === "wayTooEasy") {
    stat.wayTooEasy += 1;
    stats = { ...stats, [text.id]: stat };
    if (isSoundMasteryLevel(next.level) && !next.boostActive) {
      return progressCorrect({ ...next, textStats: stats }, corpus);
    }
    const jumped = clampLevel(Math.max(next.level, text.level) + ALGORITHM.easyJump);
    const streak = next.correctStreak + 1;
    return {
      ...next,
      textStats: stats,
      boostActive: true,
      boostLevel: jumped,
      correctStreak: streak,
      maxCorrectStreak: Math.max(next.maxCorrectStreak, streak),
      wrongStreak: 0,
    };
  }

  if (result === "right") {
    stat.correct += 1;
    if (text.kind !== "letter" && stat.correct >= ALGORITHM.retireCorrectCount) {
      stat.retired = true;
    }
    stats = { ...stats, [text.id]: stat };
    return progressCorrect({ ...next, textStats: stats }, corpus);
  }

  stat.wrong += 1;
  stat.retired = false;
  stat.cooldown = rng.int(
    text.kind === "letter" ? ALGORITHM.letterWrongCooldownMin : ALGORITHM.wrongCooldownMin,
    text.kind === "letter" ? ALGORITHM.letterWrongCooldownMax : ALGORITHM.wrongCooldownMax,
  );
  stats = { ...stats, [text.id]: stat };
  const wasBoosting = next.boostActive;
  const wrongStreak = wasBoosting ? 1 : next.wrongStreak + 1;
  return {
    ...next,
    textStats: stats,
    boostActive: false,
    correctStreak: 0,
    wrongStreak,
  };
};

const progressCorrect = (profile: Profile, corpus: ReadingText[]): Profile => {
  const streak = profile.correctStreak + 1;
  const activeLevel = workingLevel(profile);
  const shouldLevelUp = levelUpGate(profile, corpus, streak).ready;
  const level = shouldLevelUp ? clampLevel(activeLevel + 1) : profile.level;
  return {
    ...profile,
    correctStreak: shouldLevelUp ? 0 : streak,
    maxCorrectStreak: Math.max(profile.maxCorrectStreak, streak),
    wrongStreak: 0,
    level,
    boostActive: shouldLevelUp ? false : profile.boostActive,
    boostLevel: shouldLevelUp ? level : profile.boostLevel,
  };
};

export const recordAndPickNext = (
  profile: Profile,
  text: ReadingText,
  result: ResultKind,
  corpus: ReadingText[],
  rng: Rng = defaultRng,
  at = new Date(),
): Profile => {
  const showedPicture = Boolean(pictureFor(text, profile, at));
  let updated = applyResult(profile, text, result, rng, at, corpus);
  if (showedPicture) {
    updated = trackPictureSeen(updated, text.id, profile, at);
  }
  const nextText = pickNext(updated, corpus, rng);
  return { ...updated, currentTextId: nextText.id };
};

export const profileStats = (
  profile: Profile,
): { read: number; right: number; wrong: number; skip: number; wayTooEasy: number } => {
  let right = 0;
  let wrong = 0;
  let skip = 0;
  let wayTooEasy = 0;
  for (const event of profile.events) {
    if (event.result === "right") right += 1;
    if (event.result === "wrong") wrong += 1;
    if (event.result === "skip") skip += 1;
    if (event.result === "wayTooEasy") wayTooEasy += 1;
  }
  return { read: profile.events.length, right, wrong, skip, wayTooEasy };
};
